import { useEffect, useState, useRef } from 'react';
import { useSignalEffect } from '@preact/signals-react';
import styles from './Recording.module.scss';
import RecordingTimer from './RecordingTimer';
import useNrtInstance, { StartRecordingParams } from '@/hooks/useNrtInstance';
import { MicIcon, Modal, ModalBody, ModalDialog, ModalHeader, ModalTitle, StopIcon } from '@ax/ui';
import { Auth } from '@ax/auth';
import { recordingState } from '@/store/recordingState';
import useAnalytics from '@/hooks/useAnalytics';
import useMicrophoneAccess from '@/hooks/useMicrophoneAccess';
import settingsIcon from '@image/settings-icon.svg';
import {
  useAppDispatch,
  useAppSelector,
  ProviderNoteTemplate,
  visitRecordingStarted,
  visitRecordingStopped,
  navigationSlice,
} from '@ax/redux-store';
import NoteTemplatesModal from '@/components/NoteTemplatesModal/index.tsx';
import {
  selectProviderNoteTemplates,
  updateNoteTemplateRequested,
  selectNoteTemplateIdSelected,
} from '@ax/redux-store';
import { selectIsMarkdownSupportEnabled } from '@ax/redux-store';
import { selectIsNoteTemplatesEnabled } from '@ax/redux-store';

interface RecordingProps {
  readonly?: boolean;
}

const Recording = ({ readonly }: RecordingProps) => {
  const analytics = useAnalytics();
  const dispatch = useAppDispatch();
  const [isRecording, setIsRecording] = useState(false);
  const [disabledModalShown, setDisabledModalShown] = useState(false);
  const { sdkInstance, startRecording, stopRecording } = useNrtInstance();
  const visitsState = useAppSelector((state) => state.selectedVisit);
  const providerNoteTemplates = useAppSelector(selectProviderNoteTemplates);
  const noteTemplateIdSelected = useAppSelector(selectNoteTemplateIdSelected);
  const isMarkdownEnabled = useAppSelector(selectIsMarkdownSupportEnabled);
  const isNoteTemplatesEnabled = useAppSelector(selectIsNoteTemplatesEnabled);
  const noteId = visitsState?.uuid;
  const providerId = Auth.currentUser()?.guid;
  const analyticsProperties = { noteId: noteId };
  const [showModal, setShowModal] = useState(false);
  const [recordingVisitId, setRecordingVisitId] = useState<string>('');

  const isHandleStopRecordingDirectCallRef = useRef(false);

  const { hasMicrophoneAccess } = useMicrophoneAccess();

  useEffect(() => {
    if (isRecording && !hasMicrophoneAccess) {
      void handleStopRecording();
    }
  }, [hasMicrophoneAccess]);

  useSignalEffect(() => {
    if (
      !recordingState.value.recordingInprogress &&
      !isHandleStopRecordingDirectCallRef.current &&
      isRecording
    ) {
      handleStopRecording();
    }
  });

  useEffect(() => {
    return () => {
      handleStopRecording();
    };
  }, []);

  const startWithRecording = () => {
    recordingState.value = { recordingInprogress: true };

    if (noteId && providerId) {
      setIsRecording(true);

      if (sdkInstance) {
        const recordingParams: StartRecordingParams = {
          docId: providerId,
          noteId: noteId,
          streamType: 'recording',
          clientType: 'go',
          shouldTranscript: true,
          chunkDuration: 5000,
        };

        startRecording(recordingParams);
        analytics.onRecordingStart(analyticsProperties);
        setRecordingVisitId(noteId);
        dispatch(visitRecordingStarted({ visitId: noteId }));
        dispatch(
          navigationSlice.actions.unsavedChangesDetected({
            source: 'recording',
            noteId,
            unsavedChangesModal: {
              title: 'Recording in progress',
              message:
                'A recording is in progress. If you leave this note, the recording will be stopped.',
            },
          }),
        );
      }
    }
  };

  const handleStartRecording = () => {
    if (
      isMarkdownEnabled &&
      isNoteTemplatesEnabled &&
      !noteTemplateIdSelected &&
      providerNoteTemplates?.length === 1
    ) {
      dispatch(
        updateNoteTemplateRequested({
          noteId,
          templateId: providerNoteTemplates[0]?.templateId,
          regenerateNote: false,
        }),
      );
    } else if (isMarkdownEnabled && isNoteTemplatesEnabled && !noteTemplateIdSelected) {
      setShowModal(true);
      return;
    }

    startWithRecording();
  };

  const handleStopRecording = () => {
    try {
      isHandleStopRecordingDirectCallRef.current = true;
      if (recordingState.value.recordingInprogress) {
        recordingState.value = { recordingInprogress: false };
      }
      setIsRecording(false);
      setRecordingVisitId('');

      if (sdkInstance) {
        stopRecording();
        analytics.onRecordingStop(analyticsProperties);
        dispatch(visitRecordingStopped({ visitId: recordingVisitId }));
        dispatch(navigationSlice.actions.unsavedChangesResolved({ noteId }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      isHandleStopRecordingDirectCallRef.current = false;
    }
  };

  const handleOnDisabledRecordingModalHide = () => {
    setDisabledModalShown(false);
  };

  const handleKeyDownStart = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleStartRecording();
    }
  };

  const handleKeyDownStop = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleStopRecording();
    }
  };

  return (
    <div className={styles.recording__container}>
      <div
        className={`${styles.recording} ${
          (!noteId || !hasMicrophoneAccess || readonly) && styles.recording__disabled
        }`}
      >
        {isMarkdownEnabled && isNoteTemplatesEnabled && (
          <NoteTemplatesModal
            modalOpen={showModal}
            modalClose={() => {
              setShowModal(false);
            }}
            modalContinue={(newValue?: ProviderNoteTemplate) => {
              setShowModal(false);
              dispatch(
                updateNoteTemplateRequested({
                  noteId,
                  templateId: newValue?.templateId || providerNoteTemplates[0]?.templateId,
                  regenerateNote: false,
                }),
              );
              startWithRecording();
            }}
            modalData={{ noteId, providerNoteTemplates }}
          />
        )}
        {!isRecording && (
          <button
            className={styles.recording__start}
            onClick={handleStartRecording}
            onKeyDown={handleKeyDownStart}
          >
            <span className={styles.recording__start__icon__container}>
              <MicIcon
                height={16}
                width={20}
              />
            </span>
            <span className={styles.recording__start__label}>Start Recording</span>
          </button>
        )}
        {isRecording && (
          <>
            <button
              className={styles.recording__stop}
              onClick={handleStopRecording}
              onKeyDown={handleKeyDownStop}
            >
              <span className={styles.recording__stop__icon__container}>
                <StopIcon
                  height={14}
                  width={14}
                  viewBox="0 0 14 14"
                  strokeWidth={0}
                />
              </span>
            </button>
            <RecordingTimer isRecording={isRecording} />
          </>
        )}
      </div>
      {!hasMicrophoneAccess && (
        <div
          className={styles.icon__wrapper}
          onClick={() => setDisabledModalShown(true)}
        >
          <button
            className={`${styles.icon__position} cursor-pointer`}
            role="button"
            aria-label="Microphone access denied"
          >
            <div className={styles.mic__disabled}>!</div>
          </button>
        </div>
      )}

      <Modal onShow={disabledModalShown}>
        <ModalDialog>
          <ModalHeader onHide={handleOnDisabledRecordingModalHide}>
            <ModalTitle>No microphone access</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-col gap-y-1 p-2">
              <ol className="space-y-2 text-base">
                <li className="flex items-center">
                  <span>1. Click the</span>
                  <img
                    className={`${styles.modalDisabled__icon} mx-1`}
                    src={settingsIcon}
                    height={14}
                    alt={'settings icon'}
                  />
                  <span>icon in your browser's address bar</span>
                </li>
                <li>2. Turn on microphone</li>
              </ol>
            </div>
          </ModalBody>
        </ModalDialog>
      </Modal>
    </div>
  );
};

export default Recording;
